import { Component, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { filter, find, map, reduce, reverse, some, sortBy } from 'lodash';
import * as moment from 'moment';
import { CurrentSchoolYear } from 'Src/ng2/shared/constants/current-school-year.constant';
import { IStudent } from '../../../shared/typings/interfaces/student.interface';
import { StudentPanelService } from '../student-panel.service';
import AttendancePanelConfig from './student-attendance-panel.config';

@Component({
  selector: 'student-attendance-panel',
  template: require('./student-attendance-panel.component.html'),
  styles: [require('./student-attendance-panel.scss')],
  encapsulation: ViewEncapsulation.None,
})
export class StudentAttendancePanel implements OnInit {
  @Input() student: IStudent;
  @Input() collapsed: boolean;
  panelTitle: string;

  // Local
  thisYearData;
  historicalData;
  yearDataSource;
  historicalDataSource;
  yearColumns;
  historyColumns;
  showTermCol;

  constructor(private studentPanel: StudentPanelService) {}

  ngOnInit() {
    const isAnnualized = this.student.schoolDetails.termStructure === 'ANNUALIZED';
    this.showTermCol = this.student.isHS && !isAnnualized;
    this.panelTitle = AttendancePanelConfig.title;
    this.collapsed = this.studentPanel.getInitialPanelState('student-attendance-panel');
    this.yearColumns = this.showTermCol
      ? ['nameHS', 'streakHS', 'last5HS', 'last20HS', 'termHS', 'yearHS']
      : ['name', 'streak', 'last5', 'last20', 'year'];
    this.historyColumns = [
      'yearName',
      'ytd',
      'sep',
      'oct',
      'nov',
      'dec',
      'jan',
      'feb',
      'mar',
      'apr',
      'may',
      'jun',
      'overall',
    ];

    const {
      att: { currSy, currTerm, last5, last20, today, absStreak, lateStreak },
    } = this.student;
    const thisYearAbsList = this.parseDateList(currSy.daysAbs);
    const yearLateList = this.parseDateList(currSy.daysLate);
    const termAbsList = this.parseDateList(currTerm.daysAbs);
    const termLateList = this.parseDateList(currTerm.daysLate);
    const last5LateList = this.parseDateList(last5.daysLate);
    const last20LateList = this.parseDateList(last20.daysLate);
    const last5AbsList = this.parseDateList(last5.daysAbs);
    const last20AbsList = this.parseDateList(last20.daysAbs);
    // TODO: do we need logic to parse lists of consecutive late/absent days from today? (JE)
    const absStreakList = today === 'Present' ? [] : last5.daysAbs;
    const lateStreakList = today !== 'Late' ? [] : last5.daysLate;

    this.thisYearData = [
      {
        rowName: 'Days absent',
        currentStreak: absStreak || `&mdash;`,
        streakList: absStreakList,
        last5: last5.abs || `&mdash;`,
        last5List: last5AbsList,
        last20: last20.abs || `&mdash;`,
        last20List: last20AbsList,
        thisTerm: currTerm.abs || `&mdash;`,
        thisTermList: termAbsList,
        thisYear: currSy.abs || `&mdash;`,
        thisYearList: thisYearAbsList,
      },
      {
        rowName: 'Days late',
        currentStreak: lateStreak || `&mdash;`,
        streakList: lateStreakList,
        last5: last5.late || `&mdash;`,
        last5List: last5LateList,
        last20: last20.late || `&mdash;`,
        last20List: last20LateList,
        thisTerm: currTerm.late || `&mdash;`,
        thisTermList: termLateList,
        thisYear: currSy.late || `&mdash;`,
        thisYearList: yearLateList,
      },
    ];

    const history = reverse(sortBy(this.student.att.history, 'year'));

    // removes years with no attendance data (JE)
    const filteredHistory = filter(history, yearData => {
      // removes any year without attendance data (JE)
      const { sep, oct, nov, dec, jan, feb, mar, apr, may, jun, overallPctPres } = yearData;
      const props = [sep, oct, nov, dec, jan, feb, mar, apr, may, jun, overallPctPres];
      const hasData = some(props, prop => {
        return prop || prop === 0;
      });
      return hasData;
    });

    const { STARTFULL, END } = CurrentSchoolYear;
    const currentSy = `${STARTFULL}-${END}`;

    const thisYear = find(filteredHistory, historyRow => {
      return historyRow.year === `SY${currentSy}`;
    });

    // protect against missing data for this year (JE)
    if (!thisYear) {
      const emptyThisYear = {
        year: `SY${currentSy}`,
        ytd: null,
        sep: null,
        oct: null,
        nov: null,
        dec: null,
        jan: null,
        feb: null,
        mar: null,
        apr: null,
        may: null,
        jun: null,
        overallPctPres: null,
      };
      filteredHistory.unshift(emptyThisYear);
    }

    // set historical data by looping through history array
    this.historicalData = map(filteredHistory, yearData => {
      const { year, ytd, sep, oct, nov, dec, jan, feb, mar, apr, may, jun, overallPctPres } = yearData;
      return {
        rowName: year.substring(2),
        ytd: this.formatPercent(ytd),
        sep: this.formatPercent(sep),
        oct: this.formatPercent(oct),
        nov: this.formatPercent(nov),
        dec: this.formatPercent(dec),
        jan: this.formatPercent(jan),
        feb: this.formatPercent(feb),
        mar: this.formatPercent(mar),
        apr: this.formatPercent(apr),
        may: this.formatPercent(may),
        jun: this.formatPercent(jun),
        overall: this.formatPercent(overallPctPres),
      };
    });

    this.yearDataSource = new MatTableDataSource(this.thisYearData);
    this.historicalDataSource = new MatTableDataSource(this.historicalData);
  }

  formatPercent(percent: number): string {
    if (!percent && percent !== 0) return `&mdash;`;
    return `${Math.round(percent * 100)}%`;
  }

  parseDateList = (dates: string[]): string => {
    dates = reverse(
      sortBy(dates, value => {
        return new Date(value);
      }),
    );
    const shortenList = dates.length > 10;
    const extra = shortenList ? `<div class="attendance-panel-tooltip-item">+${dates.length - 10} more </div> ` : '';
    dates = shortenList ? dates.slice(0, 10) : dates;
    let parsedDates = reduce(
      dates,
      function(sum: string, date) {
        const parsedDate =
          moment().format('YYYY-MM-DD') === date
            ? '<div class="attendance-panel-tooltip-item">Today</div>'
            : '<div class="attendance-panel-tooltip-item">' + moment(date).format('MMM Do') + '</div>';
        return sum + parsedDate + ' ';
      },
      '',
    );
    parsedDates += extra;
    // cutt off extra space at end of string
    return parsedDates.substring(0, parsedDates.length - 1);
  };

  getStyleRecentTerm(days: number | string): string {
    switch (days) {
      case 0:
      case 1:
      case '&mdash;':
        return '';
      case 2:
        return 'panel-light-warn';
      case 3:
        return 'panel-warn';
      case 4:
        return 'panel-med-warn';
      default:
        return 'panel-dark-warn';
    }
  }

  getStyle20Days(days: number | string): string {
    switch (days) {
      case 0:
      case '&mdash;':
        return '';
      case 1:
        return 'panel-light-warn';
      default:
        return 'panel-dark-warn';
    }
  }

  getStyleThisTerm(days: number | string): string {
    if (days === '&mdash;' || days === 0) return '';
    else if (days >= 1 && days < 4) return 'panel-light-warn';
    else if (days >= 4 && days < 7) return 'panel-warn';
    else return 'panel-dark-warn';
  }

  getStyleThisYear(days: number | string): string {
    if (days === '&mdash;' || days === 0) return '';
    else if (days >= 1 && days < 7) return 'panel-light-warn';
    else if (days >= 7 && days < 12) return 'panel-warn';
    else if (days >= 12 && days < 19) return 'panel-med-warn';
    else return 'panel-dark-warn';
  }

  getStyleHistorical(percent: string): string {
    const num = parseInt(percent);
    if (num < 80) return 'panel-dark-warn';
    else if (num < 90) return 'panel-med-warn';
    else return '';
  }

  togglePanelBody(): void {
    this.collapsed = this.studentPanel.togglePanelBody(this.collapsed, this.panelTitle);
  }
}
